import { ROUTE_PATHS, corridorPath } from "@/routes/routePaths";
import { POPULAR_ROUTE_SLUGS, corridorRegistry } from "@/data/corridors/registry";

export type HubLink = {
  label: string;
  path: string;
  description?: string;
  distance?: string;
};

export type ContentHubSection = {
  title: string;
  intro?: string;
  links: HubLink[];
};

/** Hub 1 — Intercity ridesharing in India */
export const INTERCITY_HUB = {
  slug: "intercity-ridesharing-india",
  path: ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA,
  eyebrow: "Content hub",
  title: "Intercity ridesharing in India",
  overview:
    "Long-distance travel in India is dominated by buses, trains, and expensive outstation cabs. Duri intercity carpool connects verified hosts who already drive highways like NH65 and NH16 with guests heading the same way — split fuel and tolls, keep sedans comfortable, and book both directions on busy corridors.",
  sections: [
    {
      title: "Popular intercity carpool routes",
      intro: "Each route page includes distance, pickup zones, typical timings, and live rides.",
      links: POPULAR_ROUTE_SLUGS.map((slug) => {
        const c = corridorRegistry[slug];
        return {
          label: `${c.cityA} → ${c.cityB}`,
          path: corridorPath(slug),
          distance: `${c.stats.distanceKm} km`,
          description: `${c.stats.highway} · from ₹${c.stats.carpoolFare}/seat`,
        };
      }),
    } as ContentHubSection,
    {
      title: "Return & reverse routes",
      intro: "Many corridors have strong demand in both directions — especially weekend home travel.",
      links: [
        {
          label: "Vijayawada → Hyderabad",
          path: `${ROUTE_PATHS.INTERCITY_ROUTES}/vijayawada-to-hyderabad-carpool`,
          distance: "275 km",
          description: "Sunday evening return to Hyderabad offices.",
        },
        {
          label: "Guntur → Hyderabad",
          path: `${ROUTE_PATHS.INTERCITY_ROUTES}/guntur-to-hyderabad-carpool`,
          distance: "285 km",
          description: "Return leg after family weekends.",
        },
        {
          label: "Chennai → Nellore",
          path: `${ROUTE_PATHS.INTERCITY_ROUTES}/chennai-to-nellore-carpool`,
          distance: "180 km",
          description: "Coastal highway return pools.",
        },
        {
          label: "Tirupati → Chennai",
          path: `${ROUTE_PATHS.INTERCITY_ROUTES}/tirupati-to-chennai-carpool`,
          distance: "135 km",
          description: "Post-darshan return travel.",
        },
        {
          label: "Chittoor → Bengaluru",
          path: `${ROUTE_PATHS.INTERCITY_ROUTES}/chittoor-to-bangalore-carpool`,
          distance: "165 km",
          description: "Return pools toward Bengaluru tech parks.",
        },
      ],
    },
    {
      title: "City carpool hubs",
      intro: "Start from a city page to find daily commute and intercity routes in that metro.",
      links: [
        { label: "Hyderabad carpool", path: ROUTE_PATHS.HYDERABAD_CARPOOL, description: "Hitech City, Gachibowli & NH65 intercity." },
        { label: "Vijayawada ridesharing", path: ROUTE_PATHS.VIJAYAWADA_RIDESHARING, description: "Benz Circle & Krishna belt routes." },
        { label: "Bangalore carpool", path: ROUTE_PATHS.BANGALORE_CARPOOL, description: "Whitefield, ORR & Hyderabad corridor." },
        { label: "Chennai ridesharing", path: ROUTE_PATHS.CHENNAI_RIDESHARING, description: "OMR, Tirupati & Nellore links." },
      ],
    },
  ] as ContentHubSection[],
  relatedHubs: [
    { label: "Daily commute carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
    { label: "All popular rides", path: ROUTE_PATHS.POPULAR_ROUTES },
    { label: "How carpooling works in India", path: `${ROUTE_PATHS.GUIDES}/how-carpooling-works-india` },
  ],
};

/** Hub 2 — Daily commute (main page is DailyCommute.tsx; sub-pages linked here). */
export const DAILY_COMMUTE_HUB_LINKS: HubLink[] = [
  {
    label: "Hyderabad daily commute",
    path: ROUTE_PATHS.HYDERABAD_DAILY_COMMUTE_CARPOOL,
    description: "Hitech City, Gachibowli, Financial District office routes.",
  },
  {
    label: "Vijayawada daily commute",
    path: ROUTE_PATHS.VIJAYAWADA_DAILY_COMMUTE_CARPOOL,
    description: "Benz Circle, Auto Nagar & Guntur belt short hops.",
  },
  {
    label: "Hyderabad city hub",
    path: ROUTE_PATHS.HYDERABAD_CARPOOL,
    description: "Office commute + intercity from Hyderabad.",
  },
  {
    label: "Bangalore city hub",
    path: ROUTE_PATHS.BANGALORE_CARPOOL,
    description: "Tech park commutes & Hyderabad overnight route.",
  },
];
